/**
 * Reload the page when a lazy route chunk can no longer be fetched.
 *
 * Route components are code-split, and every chunk is named with a content
 * hash. A deploy that goes out while a tab is open leaves that tab's index
 * pointing at chunk names that no longer exist, so the first navigation to a
 * not-yet-loaded route fails with "Failed to fetch dynamically imported
 * module". Vite reports that failure as a `vite:preloadError` event; reloading
 * picks up the new index and the new chunk names.
 *
 * The event is deliberately not cancelled: Vite then rethrows the original
 * error, so it is still logged with the real cause while the reload is in
 * flight. Cancelling it would make the import resolve to `undefined` and
 * surface a misleading "Cannot read properties of undefined" instead.
 *
 * A reload is skipped when one was already triggered within the last few
 * seconds, so a chunk that is genuinely missing surfaces in the error boundary
 * instead of reloading forever. The window is short so a long-lived tab that
 * reloaded once is still protected against the next deploy.
 */
const RELOAD_AT_KEY = "stale-chunk-reloaded-at";
const RELOAD_SUPPRESS_MS = 10_000;

export function installStaleChunkReload(): void {
	window.addEventListener("vite:preloadError", () => {
		if (reloadedRecently()) {
			return;
		}

		markReloaded();
		window.location.reload();
	});
}

function markReloaded(): void {
	try {
		sessionStorage.setItem(RELOAD_AT_KEY, String(Date.now()));
	} catch {
		// Storage unavailable; a single reload without the guard is still
		// better than showing the error boundary.
	}
}

function reloadedRecently(): boolean {
	try {
		const reloadedAt = Number(sessionStorage.getItem(RELOAD_AT_KEY));
		return reloadedAt > 0 && Date.now() - reloadedAt < RELOAD_SUPPRESS_MS;
	} catch {
		return false;
	}
}
